import type { PreviewState, PreviewUiMenuItem, PreviewUiModel } from "@/components/StatusBar";
import { FALLBACK_PREVIEW_PORT } from "@/lib/preview-state";

export type PreviewAssistPhase =
  | "idle"
  | "starting"
  | "waiting_approval"
  | "retrying"
  | "failed"
  | "connection_lost"
  | "unavailable";

export type PreviewAssistError =
  | "wrong_folder"
  | "no_start_command"
  | "connection_dropped"
  | "unknown"
  | null;

interface BuildPreviewUiModelOptions {
  preview: PreviewState | null;
  previewVisible: boolean;
  activePorts: number[];
  assistPhase: PreviewAssistPhase;
  assistError: PreviewAssistError;
}

function getDefaultPort(preview: PreviewState | null): number {
  const port = preview?.defaultPort;
  return Number.isInteger(port) && (port as number) > 0
    ? (port as number)
    : FALLBACK_PREVIEW_PORT;
}

function baseLabelForPort(port: number, defaultPort: number): string {
  if (port === defaultPort || port === 3000) return "Main website";
  if (port === 6006) return "Component library";
  if (port === 4173) return "Production preview";
  if (port === 5173 || port === 5174) return "App preview";
  return "Additional preview";
}

function describePort(_port: number, label: string): string {
  if (label === "Main website") return "Best place to check the main app";
  if (label === "Component library") return "Browse components and states";
  if (label === "Production preview") return "The built version of your app";
  if (label === "App preview") return "The version updating as you work";
  return "Another live view from this project";
}

function buildPreviewTargetItems(
  preview: PreviewState | null,
  activePorts: number[],
): PreviewUiMenuItem[] {
  const defaultPort = getDefaultPort(preview);
  const seen = new Map<string, number>();

  return activePorts.map((port) => {
    const baseLabel = baseLabelForPort(port, defaultPort);
    const used = seen.get(baseLabel) ?? 0;
    seen.set(baseLabel, used + 1);
    const label = used === 0 ? baseLabel : `${baseLabel} ${used + 1}`;

    return {
      id: `open:${port}`,
      label,
      detail: describePort(port, baseLabel),
      badge: port === defaultPort ? "Recommended" : undefined,
      tone: port === defaultPort ? "accent" : "default",
      icon: "play",
    };
  });
}

function failureCopy(error: PreviewAssistError): { title: string; description: string } {
  if (error === "wrong_folder") {
    return {
      title: "Could not start from this folder",
      description: "This folder does not look like a web project yet.",
    };
  }

  if (error === "no_start_command") {
    return {
      title: "Could not find a start command",
      description: "FreshVibe could not find a usual way to run this project.",
    };
  }

  return {
    title: "Could not start the app",
    description: "FreshVibe could not get the app running successfully.",
  };
}

export function buildPreviewUiModel({
  preview,
  previewVisible,
  activePorts,
  assistPhase,
  assistError,
}: BuildPreviewUiModelOptions): PreviewUiModel | null {
  const hasPreviewSupport = Boolean(preview?.slugs?.length && preview.domain);
  if (!hasPreviewSupport) return null;

  if (activePorts.length === 1) {
    return {
      visible: true,
      label: previewVisible ? "Hide preview" : "See it",
      title: previewVisible ? "Hide the app preview" : "See what you built",
      directAction: true,
      triggerMode: "direct",
      triggerIcon: "eye",
      eyebrow: "Ready",
      tone: "success",
      active: previewVisible,
    };
  }

  if (activePorts.length > 1) {
    return {
      visible: true,
      label: "See it",
      title: "Choose what to open",
      triggerMode: "menu",
      triggerIcon: "eye",
      eyebrow: "Ready",
      tone: "success",
      active: previewVisible,
      badgeCount: activePorts.length,
      menuTitle: "Choose what to open",
      menuDescription: "FreshVibe found a few live versions of this project.",
      menuItems: buildPreviewTargetItems(preview, activePorts),
    };
  }

  if (assistPhase === "waiting_approval") {
    return {
      visible: true,
      label: "Approve preview",
      title: "FreshVibe needs your OK to open a preview",
      directAction: true,
      triggerMode: "direct",
      triggerIcon: "check",
      eyebrow: "Needs approval",
      tone: "accent",
    };
  }

  if (assistPhase === "starting") {
    return {
      visible: true,
      label: "Starting app",
      title: "FreshVibe is getting things ready",
      triggerMode: "status",
      eyebrow: "In progress",
      tone: "neutral",
      pulse: true,
    };
  }

  if (assistPhase === "retrying") {
    return {
      visible: true,
      label: "Trying again",
      title: "FreshVibe is taking another try",
      triggerMode: "status",
      eyebrow: "Retrying",
      tone: "neutral",
      pulse: true,
    };
  }

  if (assistPhase === "connection_lost") {
    return {
      visible: true,
      label: "Fix preview",
      title: "FreshVibe lost the connection before the preview was ready",
      triggerMode: "menu",
      triggerIcon: "warning",
      eyebrow: "Connection lost",
      tone: "warn",
      menuTitle: "Connection lost",
      menuDescription: "FreshVibe lost touch with the app before anything opened.",
      menuItems: [
        { id: "try_again", label: "Try again", tone: "accent", icon: "refresh" },
        { id: "reconnect", label: "Reconnect", tone: "default", icon: "refresh" },
        { id: "open_terminal", label: "Show details", tone: "default", icon: "terminal" },
      ],
    };
  }

  if (assistPhase === "failed") {
    const copy = failureCopy(assistError);
    return {
      visible: true,
      label: "Fix preview",
      title: copy.title,
      triggerMode: "menu",
      triggerIcon: "warning",
      eyebrow: "Could not start",
      tone: "warn",
      menuTitle: copy.title,
      menuDescription: copy.description,
      menuItems: [
        { id: "try_again", label: "Try again", tone: "accent", icon: "refresh" },
        { id: "ask_freshvibe", label: "Ask FreshVibe for help", tone: "default", icon: "message" },
        { id: "open_terminal", label: "Show details", tone: "default", icon: "terminal" },
      ],
    };
  }

  if (assistPhase === "unavailable") {
    return {
      visible: true,
      label: "Fix preview",
      title: "FreshVibe could not get a preview ready",
      triggerMode: "menu",
      triggerIcon: "warning",
      eyebrow: "Unavailable",
      tone: "warn",
      menuTitle: "Preview unavailable",
      menuDescription: "The app is not ready to open yet.",
      menuItems: [
        { id: "try_again", label: "Try again", tone: "accent", icon: "refresh" },
        { id: "ask_freshvibe", label: "Ask FreshVibe for help", tone: "default", icon: "message" },
        { id: "open_terminal", label: "Show details", tone: "default", icon: "terminal" },
      ],
    };
  }

  return {
    visible: true,
    label: "See it",
    title: "See what you built",
    triggerMode: "menu",
    triggerIcon: "eye",
    eyebrow: "Preview",
    tone: "neutral",
    menuTitle: "Nothing is live yet",
    menuDescription: "Choose whether FreshVibe should start the project for you or help you inspect it first.",
    menuItems: [
      { id: "start_app", label: "Start app for me", tone: "accent", icon: "play" },
      { id: "ask_freshvibe", label: "Ask FreshVibe for help", tone: "default", icon: "message" },
    ],
  };
}
